import { useEffect, useState } from "react";
import { GetUserProfileBasedOnUserSlug } from "../types/cloudFunctions/GetUserProfileBasedOnUserSlug";
import ProfileGeneralInfo from "./ProfileGeneralInfo";
import { brand } from "../../../../branchAndBrandSpecific/utils/brandVariables/brandVariables";
import { useCurrentUserProfile } from "@/store/auth/userProfile.store";
import { Box, Grid } from "@radix-ui/themes";
import { fetchRecords } from "../helperFunctions/fetchRecords";

export default function ProfileAndCommunity({
  slug,
  setIsFriendsOfCurrentUser,
  children,
}: {
  slug: string;
  setIsFriendsOfCurrentUser: React.Dispatch<React.SetStateAction<boolean>>;
  children: React.ReactNode;
}) {
  // Loading and error states
  const [loading, setLoading] = useState<Boolean>(true);
  const [error, setError] = useState<Error | null>(null);

  // Get current user profile from store. No Calls needed
  const currentUserProfile = useCurrentUserProfile((s) => s.user);

  // Check if the profile belongs to the current user
  const belongToCurrentUser = !slug || slug === currentUserProfile.slug;

  // Create a state to store the user profile
  const [userProfile, setUserProfile] =
    useState<GetUserProfileBasedOnUserSlug | null>(null);

  useEffect(() => {
    // If the profile belongs to the current user, set the user profile to the current user profile
    if (belongToCurrentUser) {
      setUserProfile({
        ...currentUserProfile,
        belongsToCurrentUser: true,
        isFriendsOfCurrentUser: false,
        isFollowedByCurrentUser: false,
        gamesWithLinkedAccounts: [],
        isFriendsRequestPending: false,
        receivedFriendRequest: false,
      });
    } else {
      // if it doesn't belong to the current user, fetch the profile data
      fetchRecords(brand.tenantId, slug, setUserProfile, setLoading, setError);
    }
  }, [slug]);

  // If curreny account is being updated by user, refresh details
  useEffect(() => {
    if (belongToCurrentUser) {
      setUserProfile({
        ...currentUserProfile,
        belongsToCurrentUser: true,
        isFriendsOfCurrentUser: false,
        isFollowedByCurrentUser: false,
        gamesWithLinkedAccounts: [],
        isFriendsRequestPending: false,
        receivedFriendRequest: false,
      });
    }
  }, [currentUserProfile]);

  useEffect(() => {
    if (userProfile) {
      setIsFriendsOfCurrentUser(userProfile?.isFriendsOfCurrentUser);
    }
  }, [userProfile]);

  useEffect(() => {
    setUserProfile({
      ...currentUserProfile,
      belongsToCurrentUser: true,
      isFriendsOfCurrentUser: false,
      isFollowedByCurrentUser: false,
      gamesWithLinkedAccounts: [],
      isFriendsRequestPending: false,
      receivedFriendRequest: false,
    });
    setLoading(false);
  }, [slug]);

  // Variables
  const hasFriendsFeature = brand.hasFeature.friends;

  if (error) {
    return <div>Error: {error.message}</div>;
  }

  if (loading) {
    return <div>Loading...</div>;
  }

  return (
    <Grid gap="3" columns={{ initial: "1", md: "12" }}>
      <Box
        gridColumnStart={{ initial: "1", md: "1" }}
        gridColumnEnd={{ initial: "1", md: "4" }}
      >
        {userProfile && (
          <ProfileGeneralInfo
            profileLogoUrl={userProfile.profileLogoUrl}
            profileLogoUrlHistory={userProfile.profileLogoUrlHistory}
            userName={userProfile.userName}
            fullName={userProfile.fullName}
            belongsToCurrUser={userProfile.belongsToCurrentUser}
            isFollowedByCurrentUser={userProfile.isFollowedByCurrentUser}
            isFriendsOfCurrentUser={userProfile.isFriendsOfCurrentUser}
            isFriendsRequestPending={userProfile.isFriendsRequestPending}
            receivedFriendRequest={userProfile.receivedFriendRequest}
            // gamesWithLinkedAccounts={userProfile?.gamesWithLinkedAccounts}
            // isOnline={isOnline}
            uid={userProfile?.uid}
            isPlaceholder={false}
            country={userProfile.country}
            profileDescription={userProfile.profileDescription}
            slug={userProfile.slug}
            followersCount={userProfile.followersCount}
            friendsCount={userProfile.friendsCount}
            // community={community}
            lang={userProfile.lang || "en"}
            allPlatforms={userProfile?.allPlatforms}
            termsAccepted={userProfile.termsAccepted}
          />
        )}

        {/* <ProfileAchievements /> */}
      </Box>

      <Box
        gridColumnStart={{ initial: "1", md: "4" }}
        gridColumnEnd={{ initial: "1", md: "13" }}
      >
        {children}
      </Box>
    </Grid>
  );
}
